'use client'

import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import { track } from '@/utils/analytics'
import { getBrowserAnalyticsConsent, subscribeToAnalyticsConsent } from '@/utils/analyticsConsent'
import { READER_SUPPORT_OFFER, READER_SUPPORT_URL, readerSupportUrl, supportPosition, type SupportSurface } from '@/utils/readerSupport'
import { observeSupportImpression } from '@/utils/observeSupportImpression'

export function SupportHeart({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" />
    </svg>
  )
}

export default function ResearchSupportLink({ surface, postSlug, postTopic, className = '', children, onClick }: {
  surface: SupportSurface
  postSlug?: string
  postTopic?: string
  className?: string
  children?: ReactNode
  onClick?: () => void
}) {
  const pathname = usePathname() || '/'
  const ref = useRef<HTMLAnchorElement>(null)
  const seen = useRef('')
  const consent = useSyncExternalStore(subscribeToAnalyticsConsent, getBrowserAnalyticsConsent, () => null)
  const key = `${pathname}:${surface}`
  useEffect(() => { seen.current = '' }, [key])
  const params = {
    cta_surface: surface,
    cta_position: supportPosition(surface),
    page_path: pathname,
    page_type: pathname.startsWith('/blog/') ? 'blog' : pathname === '/' ? 'home' : 'static',
    post_slug: postSlug || (pathname.startsWith('/blog/') ? pathname.slice(6) : undefined),
    post_topic: postTopic,
    destination: READER_SUPPORT_URL,
    offer_id: READER_SUPPORT_OFFER,
  }

  useEffect(() => {
    const element = ref.current
    if (!element || consent !== 'granted' || typeof IntersectionObserver === 'undefined') return
    return observeSupportImpression(element,
      () => getBrowserAnalyticsConsent() === 'granted' && seen.current !== key,
      () => {
        seen.current = key
        track('support_cta_impression', {
          cta_surface: surface, cta_position: supportPosition(surface), page_path: pathname,
          page_type: pathname.startsWith('/blog/') ? 'blog' : pathname === '/' ? 'home' : 'static',
          post_slug: postSlug || (pathname.startsWith('/blog/') ? pathname.slice(6) : undefined),
          post_topic: postTopic, destination: READER_SUPPORT_URL, offer_id: READER_SUPPORT_OFFER,
          trigger: 'visible_1s',
        })
      })
  }, [consent, key, pathname, surface, postSlug, postTopic])

  const captureClick = () => {
    // A quick tap proves visibility too. Include it in the denominator even
    // when the reader acts before the one-second view timer finishes.
    if (getBrowserAnalyticsConsent() === 'granted' && seen.current !== key) {
      seen.current = key
      track('support_cta_impression', { ...params, trigger: 'interaction' })
    }
    track('support_cta_click', { ...params, external_completion_required: true })
    onClick?.()
  }

  return (
    <a ref={ref} href={readerSupportUrl(surface)} target="_blank" rel="noopener noreferrer"
      data-support-surface={surface} onClick={captureClick}
      onAuxClick={(event) => { if (event.button === 1) captureClick() }}
      className={`inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-control text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 ${className}`}>
      {children || <><SupportHeart />Support our research</>}
      <span className="sr-only"> (opens Buy Me a Coffee in a new tab)</span>
    </a>
  )
}
